import React from 'react';
import styleable from 'react-styleable';
import PropTypes from 'prop-types';
import Loader from 'halogen/PulseLoader';
import { injectIntl, intlShape } from 'react-intl';
import css from './index.scss';
import TeacherHeader from '../teacher-header';
import TeacherFilter from '../teacher-filter';
import { TeacherRowRenderer } from '../renderers';
import * as gridFormatters from '../formatters';
import EmptyState from '../empty-state';
import TeacherFooter from '../teacher-footer';
import Modal from '../modal';
import AddTeacher from '../add-teacher';
import { StudentManagement, Tick } from '../../helpers/icon-dictionary';
import AddTeacherConfirmation from '../teacher-confirmation';
import SendEmailResult from '../send-email-result';
import messages from './messages';
import DataGrid from '../data-grid/index';

function getGridColumns(selectedProduct, handleGridSort, handleOnTeacherEdit, intl, handleOnAssignClass) {
    return [
        {
            key: 'displayTitle',
            name: `${intl.formatMessage(messages.title)}`,
            resizable: true,
            width: 70,
            sortable: true,
            headerRenderer: <gridFormatters.HeaderFormatter onSort={handleGridSort} />
        },
        {
            key: 'firstName',
            name: `${intl.formatMessage(messages.firstName)}`,
            resizable: true,
            width: 160,
            sortable: true,
            headerRenderer: <gridFormatters.HeaderFormatter onSort={handleGridSort} />
        },
        {
            key: 'lastName',
            name: `${intl.formatMessage(messages.surname)}`,
            resizable: true,
            width: 160,
            sortable: true,
            headerRenderer: <gridFormatters.HeaderFormatter onSort={handleGridSort} />
        },
        {
            key: 'emailAddress',
            name: `${intl.formatMessage(messages.emailAddress)}`,
            resizable: true,
            width: 300,
            sortable: true,
            headerRenderer: <gridFormatters.HeaderFormatter onSort={handleGridSort} />
        },
        {
            key: 'userName',
            name: `${intl.formatMessage(messages.username)}`,
            resizable: true,
            width: 150,
            sortable: true,
            headerRenderer: <gridFormatters.HeaderFormatter onSort={handleGridSort} />
        },
        {
            key: 'class',
            name: `${selectedProduct.name.toUpperCase()} ${intl.formatMessage(messages.classes)}`,
            width: 250,
            resizable: true,
            getRowMetaData: row => row,
            formatter: <gridFormatters.AssignedClassFormatter handleOnAssignClass={handleOnAssignClass} />
        },
        {
            key: 'emailStatus',
            name: `${intl.formatMessage(messages.accountStatus)}`.toUpperCase(),
            resizable: true,
            width: 200,
            sortable: true,
            formatter: <gridFormatters.EmailStatusFormatter />,
            headerRenderer: <gridFormatters.HeaderFormatter onSort={handleGridSort} />
        },
        {
            key: '',
            name: ''
        },
        {
            key: 'edit',
            name: `${intl.formatMessage(messages.edit)}`,
            width: 70,
            locked: true,
            getRowMetaData: row => row,
            formatter: <gridFormatters.EditInfoFormatter handleOnEdit={handleOnTeacherEdit} />
        }
    ];
}

const TeacherRoll = ({
    teachers,
    teacherTitles,
    teachersCount,
    selectedTeachers = [],
    isTeacherDataLoaded,
    isTeacherRollDownloaded,
    gridRowGetter,
    onGridSort,
    onRowSelect,
    onDownloadTeacherRoll,
    onQuickUpload,
    onAddTeacher,
    onAddAnotherTeacher,
    dataFilterOptions,
    filterOnChange,
    filterOnClear,
    onSearch,
    selectedProduct,
    handleOnTeacherUnassign,
    showModal,
    handleOnCloseModal,
    handleOnUnassignConfirm,
    isUserOperationModalOpen,
    isEmailVerificationModalOpen,
    sendingEmailsResult,
    emailVerificationError,
    handleOnCloseEmailVerificationModal,
    isLoading,
    showAddTeachersModal,
    onAddTeacherCloseModal,
    classrooms,
    teacher,
    isSaveInProgress,
    handleOnAddTeacherSubmit,
    showAddTeacherValidationModal,
    addTeacherValidationMessage,
    closeAddTeacherValidationModal,
    showAddTeacherConfirmationModal,
    isSignInCardDownloaded,
    handleOnPrintSignInCard,
    handleOnPrintMultipleSignInCard,
    handleOnTeacherEdit,
    intl,
    handleOnAssignClass,
    filteredTeachersCount,
    handleOnTeacherDeleteOpenModal,
    handleOnDeleteTeacher,
    operationType,
    teachersOperationSuccessConfirmation,
    deletingTeacher
}) => {
    teacherTitles[0].value = intl.formatMessage(messages.teacherTitleNone);
    teacherTitles[0].value = intl.formatMessage(messages.teacherTitleNone_key);
    teacherTitles[5].value = intl.formatMessage(messages.dr);
    teacherTitles[5].key = intl.formatMessage(messages.dr_key);
    teacherTitles[1].value = intl.formatMessage(messages.miss);
    teacherTitles[1].key = intl.formatMessage(messages.miss_key);
    teacherTitles[2].value = intl.formatMessage(messages.mr);
    teacherTitles[2].key = intl.formatMessage(messages.mr_key);
    teacherTitles[3].value = intl.formatMessage(messages.mrs);
    teacherTitles[3].key = intl.formatMessage(messages.mrs_key);
    teacherTitles[4].value = intl.formatMessage(messages.ms);
    teacherTitles[4].key = intl.formatMessage(messages.ms_key);
    const sortedTitles = teacherTitles.slice(1).sort((a, b) => a.value.localeCompare(b.value));
    sortedTitles.unshift(teacherTitles[0]);

    const loaderView = (
        <div className={css.teacherListContainer}>
            <div className={css.loadingContainer}>
                <Loader className={css.loader} />
            </div>
        </div>);

    const loadAddTeacherModalComponent = () => {
        let modalComponent = '';
        if (showAddTeacherConfirmationModal) {
            modalComponent = (
                <AddTeacherConfirmation
                    handleOnAddTeacher={onAddAnotherTeacher}
                    handleOnCloseModal={onAddTeacherCloseModal}
                    handleOnPrintSignInCard={handleOnPrintSignInCard}
                    icon={Tick}
                    isSignInCardDownloaded={isSignInCardDownloaded}
                    title={intl.formatMessage(messages.addTeacherConfirmation)}
                />
            );
        } else {
            modalComponent = (
                <AddTeacher
                    addTeacherValidationMessage={addTeacherValidationMessage}
                    classes={classrooms}
                    closeAddTeacherValidationModal={closeAddTeacherValidationModal}
                    handleOnAddTeacherSubmit={handleOnAddTeacherSubmit}
                    handleOnCloseModal={onAddTeacherCloseModal}
                    isSaveInProgress={isSaveInProgress}
                    selectedProduct={selectedProduct}
                    showAddTeacherValidationModal={showAddTeacherValidationModal}
                    teacher={teacher}
                    teacherTitles={sortedTitles}
                />);
        }

        return modalComponent;
    };

    const addTeacherModalView = (
        <Modal show={showAddTeachersModal} >
            {loadAddTeacherModalComponent()}
        </Modal>
    );

    const emailVerificationModalView = (
        <Modal show={isEmailVerificationModalOpen} smallMode>
            <SendEmailResult
                fatalError={emailVerificationError}
                onCloseModal={handleOnCloseEmailVerificationModal}
                sendingEmailsResult={sendingEmailsResult}
            />
        </Modal>
    );

    const emptyStateView = (
        <div className={css.teacherListContainer}>
            <TeacherFilter
                classrooms={dataFilterOptions.classrooms}
                handleFilterOnChange={filterOnChange}
                handleFilterOnClear={filterOnClear}
                handleOnSearch={onSearch}
                lastNames={dataFilterOptions.lastNames}
                searchTerm={dataFilterOptions.searchTerm} />
            <EmptyState iconName={StudentManagement} message={intl.formatMessage(messages.noTeachersAvailable)} />
            {addTeacherModalView}
        </div>);

    const dataView = (
        <div className={css.teacherListContainer}>
            <TeacherFilter
                classrooms={dataFilterOptions.classrooms}
                handleFilterOnChange={filterOnChange}
                handleFilterOnClear={filterOnClear}
                handleOnSearch={onSearch}
                lastNames={dataFilterOptions.lastNames}
                searchTerm={dataFilterOptions.searchTerm} />
            <DataGrid
                columns={getGridColumns(selectedProduct, onGridSort, handleOnTeacherEdit, intl, handleOnAssignClass)}
                handleGridSort={onGridSort}
                handleRowSelect={onRowSelect}
                overScan={{ colsStart: 17, colsEnd: 17, rowsStart: 25, rowsEnd: 25 }}
                rowGetter={gridRowGetter}
                rowHeight={150}
                rowKey="userProfileId"
                rowRenderer={TeacherRowRenderer}
                rowsCount={teachers.length}
                selectedRows={selectedTeachers.length > 0 ? selectedTeachers : []}
            />
            {addTeacherModalView}
            {emailVerificationModalView}
        </div>);

    let finalView;
    if (!isTeacherDataLoaded) {
        finalView = loaderView;
    } else {
        finalView = filteredTeachersCount === 0 ? emptyStateView : dataView;
    }
    const teachersComponent = (<div className={css.teacherRollComponent}>
        <TeacherHeader
            filteredTeachersCount={teachers.length}
            handleAddTeacher={onAddTeacher}
            handleOnDownloadTeacherRoll={onDownloadTeacherRoll}
            handleQuickUpload={onQuickUpload}
            isTeacherDataLoaded={isTeacherDataLoaded}
            isTeacherRollDownloaded={isTeacherRollDownloaded}
            teachersCountInSchool={teachersCount} />
        {finalView}
        <TeacherFooter
            deletingTeacher={deletingTeacher}
            handleOnCloseModal={handleOnCloseModal}
            handleOnDeleteTeacher={handleOnDeleteTeacher}
            handleOnDownloadSignInCards={handleOnPrintMultipleSignInCard}
            handleOnTeacherDeleteOpenModal={handleOnTeacherDeleteOpenModal}
            handleOnTeacherUnassign={handleOnTeacherUnassign}
            handleOnUnassignConfirm={handleOnUnassignConfirm}
            isLoading={isLoading}
            isSignInCardDownloaded={isSignInCardDownloaded}
            isUserOperationModalOpen={isUserOperationModalOpen}
            noOfSelectedTeachers={selectedTeachers.length}
            operationType={operationType}
            showModal={showModal}
            teachersOperationSuccessConfirmation={teachersOperationSuccessConfirmation} />
    </div>);

    return (
        <div className={css.teacherRollComponent}>
            {teachersComponent}
        </div>
    );
};

TeacherRoll.propTypes = {
    isTeacherDataLoaded: PropTypes.bool,
    isTeacherRollDownloaded: PropTypes.bool,
    showAddTeacherConfirmationModal: PropTypes.bool,
    teacher: PropTypes.object,
    teachers: PropTypes.arrayOf(PropTypes.object),
    teacherTitles: PropTypes.arrayOf(PropTypes.object),
    classrooms: PropTypes.arrayOf(PropTypes.object),
    selectedTeachers: PropTypes.arrayOf(PropTypes.number),
    gridRowGetter: PropTypes.func,
    teachersCount: PropTypes.number,
    filteredTeachersCount: PropTypes.number,
    onGridSort: PropTypes.func,
    onRowSelect: PropTypes.func,
    onDownloadTeacherRoll: PropTypes.func,
    onQuickUpload: PropTypes.func,
    onAddTeacher: PropTypes.func,
    closeAddTeacherValidationModal: PropTypes.func,
    dataFilterOptions: PropTypes.object,
    filterOnChange: PropTypes.func,
    filterOnClear: PropTypes.func,
    onSearch: PropTypes.func,
    onAddTeacherCloseModal: PropTypes.func,
    handleOnAddTeacherSubmit: PropTypes.func,
    selectedProduct: PropTypes.object,
    showAddTeachersModal: PropTypes.bool,
    isSaveInProgress: PropTypes.bool,
    showAddTeacherValidationModal: PropTypes.bool,
    isSignInCardDownloaded: PropTypes.bool,
    addTeacherValidationMessage: PropTypes.string,
    handleOnPrintSignInCard: PropTypes.func,
    handleOnPrintMultipleSignInCard: PropTypes.func,
    handleOnTeacherUnassign: PropTypes.func,
    handleOnCloseModal: PropTypes.func,
    showModal: PropTypes.bool,
    handleOnUnassignConfirm: PropTypes.func,
    isUserOperationModalOpen: PropTypes.bool,
    isEmailVerificationModalOpen: PropTypes.bool,
    sendingEmailsResult: PropTypes.object,
    emailVerificationError: PropTypes.bool,
    handleOnCloseEmailVerificationModal: PropTypes.func,
    isLoading: PropTypes.bool,
    teachersOperationSuccessConfirmation: PropTypes.func,
    onAddAnotherTeacher: PropTypes.func,
    handleOnTeacherEdit: PropTypes.func,
    intl: intlShape.isRequired,
    handleOnAssignClass: PropTypes.func,
    handleOnTeacherDeleteOpenModal: PropTypes.func,
    handleOnDeleteTeacher: PropTypes.func,
    operationType: PropTypes.string,
    deletingTeacher: PropTypes.bool
};

export default injectIntl(styleable(css)(TeacherRoll));



// WEBPACK FOOTER //
// ./app/components/teacher-roll/index.js